// Onboarding wizard form schemas — zod, per the EPIC-02 spec.
//
// Why local: the wizard state is persisted as `users.onboarding_progress`
// (a JSON column owned by the API), so the source of truth is the API
// response shape. We re-derive the runtime types here so each step component
// can use `react-hook-form` + `zodResolver` like the auth feature.
//
// Step coverage:
//   - Step 1 (Welcome) — no form
//   - Step 2 (Goal)    — `goalSchema`
//   - Step 3 (Connect) — `connectSchema`
//   - Step 4 (AIDemo)  — no form (animation-only)
//   - Step 5 (Plan)    — `planSchema`
//   - Step 6 (Payment) — `paymentSchema`

import { z } from 'zod';

import {
  ONBOARDING_GOALS,
  DEFAULT_INBOX_MODES,
  type DefaultInboxMode,
  type OnboardingGoal,
} from '@barel/core';

export const ONBOARDING_PLANS = ['discover', 'scale', 'agency'] as const;
export type OnboardingPlan = (typeof ONBOARDING_PLANS)[number];

export const CONNECT_METHODS = ['extension', 'cookie'] as const;
export type ConnectMethod = (typeof CONNECT_METHODS)[number];

export const goalSchema = z
  .object({
    goal: z.enum(ONBOARDING_GOALS),
    inboxMode: z.enum(DEFAULT_INBOX_MODES).optional(),
  })
  .superRefine((value, ctx) => {
    if ((value.goal === 'inbox' || value.goal === 'both') && !value.inboxMode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['inboxMode'],
        message: 'inboxMode_required',
      });
    }
  });
export type GoalFormValues = z.infer<typeof goalSchema>;

export const connectSchema = z.object({
  method: z.enum(CONNECT_METHODS),
  cookie: z.string().trim().max(2048).optional(),
});
export type ConnectFormValues = z.infer<typeof connectSchema>;

export const planSchema = z.object({
  plan: z.enum(ONBOARDING_PLANS),
});
export type PlanFormValues = z.infer<typeof planSchema>;

/**
 * Payment step form — Stripe Elements owns the card fields (never posted
 * through our API, US-028); we only validate the cardholder name + the
 * billing country (feeds Stripe Tax via `/billing/start-trial`).
 */
export const paymentSchema = z.object({
  name: z.string().trim().min(2, 'name_required').max(120),
  country: z.string().length(2, 'country_invalid'),
});
export type PaymentFormValues = z.infer<typeof paymentSchema>;

export type WizardGoal = OnboardingGoal;
export type WizardInboxMode = DefaultInboxMode;
